import { DashedTag } from '@praetorian-chariot/ui';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';

// Chip display variant styles
const CHIP_VARIANTS = {
  default: '',
  brand: 'bg-chariot-brand-primary border-none text-white',
  info: 'bg-chariot-surface-secondary border-chariot-stroke',
  filter: 'bg-chariot-background-secondary border-chariot-stroke',
} as const;

type ChipVariant = keyof typeof CHIP_VARIANTS;

// Generic props interface for enhanced flexibility
interface ChipsListProps<T = string> {
  items: T[];
  maxVisible?: number;
  className?: string;
  chipClassName?: string;
  moreChipClassName?: string;
  tooltipClassName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: ChipVariant;

  // Custom rendering support
  renderChip?: (item: T, index: number) => React.ReactNode;
  renderMoreChip?: (count: number) => React.ReactNode;

  // Event handlers
  onChipClick?: (item: T, index: number) => void;
  onMoreClick?: () => void;

  // Tooltip behavior
  wrapInTooltip?: boolean; // Wrap entire container in tooltip instead of individual chips
  disableTooltips?: boolean; // Disable tooltips for dropdown contexts where tooltips interfere

  // Accessibility
  'data-testid'?: string;
  ariaLabel?: string;
}

// Helper function to get variant classes
const getVariantClassName = (variant: ChipVariant): string => {
  return CHIP_VARIANTS[variant];
};

// Default chip renderer for string items
const defaultStringRenderer = (
  item: string,
  index: number,
  size: 'xs' | 'sm' | 'md' | 'lg',
  variant: ChipVariant,
  chipClassName: string,
  onChipClick?: (item: string, index: number) => void
): React.ReactNode => (
  <DashedTag
    key={`${item}-${index}`}
    label={item}
    size={size}
    className={`${getVariantClassName(variant)} ${chipClassName}`.trim()}
    onClick={onChipClick ? () => onChipClick(item, index) : undefined}
  />
);

/**
 * Enhanced chips display component with generic type support, variants, and custom rendering.
 * Features:
 * - Generic type support for any item type
 * - Built-in variant styles (default, brand, info, filter)
 * - Custom chip and "more" chip rendering
 * - Click handler support
 * - Backward compatible with existing string-based usage
 * - Responsive and space-efficient for table columns
 * - Configurable tooltip behavior (individual chips or entire container)
 *
 * @example
 * // Basic usage (backward compatible)
 * <ChipsList items={['tag1', 'tag2']} />
 *
 * // With variants
 * <ChipsList items={sources} variant="brand" maxVisible={3} />
 *
 * // With custom rendering
 * <ChipsList
 *   items={objects}
 *   renderChip={(obj, idx) => <CustomChip key={idx} data={obj} />}
 * />
 *
 * // Wrap entire container in tooltip (useful for table cells)
 * <ChipsList items={origins} wrapInTooltip />
 */
export const ChipsList = <T = string,>({
  items = [] as T[],
  maxVisible = 1,
  className = '',
  chipClassName = '',
  moreChipClassName = '',
  tooltipClassName = '',
  size = 'xs',
  variant = 'default',
  renderChip,
  renderMoreChip,
  onChipClick,
  onMoreClick,
  wrapInTooltip = false,
  disableTooltips = false,
  'data-testid': dataTestId,
  ariaLabel,
}: ChipsListProps<T>): React.ReactElement | null => {
  if (!items.length) return <span className="text-chariot-text-muted">-</span>;

  // Use custom renderer or default string renderer
  const chipRenderer =
    renderChip ||
    ((item: T, index: number) => {
      // Fallback to string rendering for backward compatibility
      return defaultStringRenderer(
        String(item),
        index,
        size,
        variant,
        chipClassName,
        onChipClick as ((item: string, index: number) => void) | undefined
      );
    });

  // Single item - no tooltip needed
  if (items.length === 1) {
    return (
      <div
        className={`flex ${className}`.trim()}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        {chipRenderer(items[0], 0)}
      </div>
    );
  }

  // Multiple items - show visible + counter with tooltip
  const visibleItems = items.slice(0, maxVisible);
  const remainingItems = items.slice(maxVisible);
  const remainingCount = remainingItems.length;

  // Tooltip content showing ALL items
  const allItemsTooltip = (
    <div className={`flex max-w-md flex-wrap gap-2 ${tooltipClassName}`.trim()}>
      {items.map((item, index) => chipRenderer(item, index))}
    </div>
  );

  // Content to display (visible items + "+N more" if applicable)
  const content = (
    <div
      className={`flex items-center gap-1 ${className}`.trim()}
      data-testid={dataTestId}
      aria-label={ariaLabel}
    >
      {disableTooltips || wrapInTooltip ? (
        // When disableTooltips is true or wrapInTooltip is true, don't wrap individual elements
        <>
          <span>
            {visibleItems.map((item, index) => chipRenderer(item, index))}
          </span>
          {remainingCount > 0 && (
            <span>
              {renderMoreChip ? (
                renderMoreChip(remainingCount)
              ) : (
                <DashedTag
                  label={`+${remainingCount}`}
                  size={size}
                  className={`bg-chariot-surface-tertiary text-chariot-text-secondary hover:bg-chariot-surface-quaternary cursor-pointer transition-colors ${moreChipClassName}`.trim()}
                  onClick={onMoreClick}
                />
              )}
            </span>
          )}
        </>
      ) : (
        // Original behavior: wrap individual elements with tooltips
        <>
          {/* Show visible chip(s) with tooltip showing all items */}
          <Tooltip title={allItemsTooltip}>
            <span className="cursor-pointer">
              {visibleItems.map((item, index) => chipRenderer(item, index))}
            </span>
          </Tooltip>
          {/* Show "+N more" with tooltip showing all items */}
          {remainingCount > 0 && (
            <Tooltip title={allItemsTooltip}>
              <span>
                {renderMoreChip ? (
                  renderMoreChip(remainingCount)
                ) : (
                  <DashedTag
                    label={`+${remainingCount}`}
                    size={size}
                    className={`bg-chariot-surface-tertiary text-chariot-text-secondary hover:bg-chariot-surface-quaternary cursor-pointer transition-colors ${moreChipClassName}`.trim()}
                    onClick={onMoreClick}
                  />
                )}
              </span>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );

  // When wrapInTooltip is true and tooltips are not disabled, wrap the entire container
  if (wrapInTooltip && !disableTooltips) {
    return (
      <Tooltip title={allItemsTooltip}>
        <div className="cursor-pointer">{content}</div>
      </Tooltip>
    );
  }

  return content;
};

ChipsList.displayName = 'ChipsList';
